import { useEffect, useRef, useState } from "react";
import { IOrbProps } from "./App";
import { useApi } from './components/useApi';
import { useUserContext } from './components/useUserContext';
import { useDataContext } from './components/useDataContext';
import { useIntegration } from './components/useIntegration';
import React from "react";
import { HashRouter, Switch, Route } from 'react-router-dom';
import { ErrorBoundary } from "./components/ErrorBoundary";
import { IntegrationContext } from "./components/IntegrationContext";
import { DataContext } from "./components/DataContext";
import { OrbUserContext } from "./components/OrbUserContext";
import { CrLoadingOverlay } from './components/cr/CrLoadingOverlay';
import { AppGlobals, AppIDs } from "./AppGlobals";
import { ManageProjectsMenu } from "./components/ManageProjectsMenu";
import PerformanceReportingAdmin from "./components/PerformanceReportingAdmin";
import { withErrorHandling } from './components/withErrorHandling';
import {
    AdminRoute,
    DirectorateAdminRoute,
    MilestoneAdminRoute,
    NotAuthorised,
    NotFound,
    ProjectAdminRoute
} from "./Routes";
import ProjectUpdates from "./components/project/ProjectUpdates";
import PortfolioUpdates from "./components/portfolio/PortfolioUpdates";
import ProgrammeUpdates from "./components/programme/ProgrammeUpdates";
import LoadingWhite from "./LoadingWhite";
import RiskUpdates from "./components/risk/RiskUpdates";
import ProjectSignOff from "./components/project/ProjectSignOff";
import RiskSignOff from "./components/risk/RiskSignOff";
import PortfolioSignOff from "./components/portfolio/PortfolioSignOff";
import { ManageProjectsWelcome } from "./components/manageProjects/ManageProjectsWelcome";
import SystemAdministration from "./components/SystemAdministration";
import { Insights } from "./components/Insights";
import { useContentStyle } from "./components/ContentStyleContext";
import ProgrammeSignOff from "./components/programme/ProgrammeSignOff";
import { TopBanner } from "./TopBanner";
import { MenuToggle } from "./components/MenuToggle";
import { buildManageProjectsBreadcrumb } from "./breadcrumbs/manageProjectsBreadcrumb";
import { useHistory, useLocation } from "react-router-dom";
import { ICustomerFinancialYear, IEntityDescription1 } from "./types";
import AiChat from "./components/AI/AiChat";
import { CustomerFinancialYear } from "./components/settings/CustomerFinancialYear";
import ManageProjectsReport from "./components/manageProjects/ManageProjectsReport";

const ScrollToTop: React.FunctionComponent = () => {
    const location = useLocation();

    useEffect(() => {
        const main = document.querySelector('.main');
        if (main) main.scrollTop = 0;
    }, [location.pathname]);

    return null;
};

const ManageProjectsApp: React.FunctionComponent<IOrbProps> = (props: IOrbProps) => {

    const history = useHistory();
    const location = useLocation();
    const [entityDescriptionForMenu, setEntityDescriptionForMenu] = useState<IEntityDescription1>();
    const [lastObjectiveIdForChilds, setLastObjectiveIdForChilds] = useState(null);
    const [lastVisionIdForChilds, setLastVisionIdForChilds] = useState(null);
    const [lastPortfolioIdForChilds, setLastPortfolioIdForChilds] = useState(null);
    const [lastProgrammeIdForChilds, setLastProgrammeIdForChilds] = useState(null);
    const [lastProjectIdForChilds, setLastProjectIdForChilds] = useState(null);
    const [lastDirectorateIdForChilds, setLastDirectorateIdForChilds] = useState(null);
    const [customerFinancialYear, setCustomerFinancialYear] = useState<ICustomerFinancialYear>(null);

    // Left-nav expand/collapse state — lifted up here (rather than
    // living inside ManageProjectsMenu) so it survives ManageProjectsMenu
    // remounting for any reason (e.g. the mobile drawer toggle).
    const [manageNavExpanded, setManageNavExpanded] = useState(true);       // Manage OPEN by default
    const [settingsNavExpanded, setSettingsNavExpanded] = useState(false);
    const [financialsNavExpanded, setFinancialsNavExpanded] = useState(false); // Financials CLOSED by default
    const [hasInitialisedNavFromRoute, setHasInitialisedNavFromRoute] = useState(false);

    const { dataServices, errorHandling } = props;

    useEffect(() => {
        (async () => {
            const fy: ICustomerFinancialYear = await dataServices.accountService.getCustomerFinancialYear();
            setCustomerFinancialYear(fy);
        })();
    }, []);

    const apiConnected = useApi(dataServices);
    const uc = useUserContext(apiConnected, dataServices, errorHandling);
    const ld = useDataContext(apiConnected, dataServices, errorHandling);

    const integration = useIntegration({
        disableDirectorateManagement: false,
        disableGroupManagement: false,
        disablePartnerOrganisationManagement: false,
        disableProjectManagement: false,
        disableUserManagement: false,
        dataSourceName: ''
    });

    const routeProps = {
        ...props,
        menuData: props.menuData,
        apiConnected: apiConnected,
        entityDescriptionForMenu: entityDescriptionForMenu,
        onEntityDescriptionChange: setEntityDescriptionForMenu,
        ...uc,
        ...ld
    };

    const commonProps = {
        lastObjectiveIdForChilds,
        setLastObjectiveIdForChilds,
        lastVisionIdForChilds,
        setLastVisionIdForChilds,
        lastPortfolioIdForChilds,
        setLastPortfolioIdForChilds,
        lastProgrammeIdForChilds,
        setLastProgrammeIdForChilds,
        lastProjectIdForChilds,
        setLastProjectIdForChilds,
        lastDirectorateIdForChilds,
        setLastDirectorateIdForChilds,
        customerFinancialYear,
        setCustomerFinancialYear,
        ...routeProps
    };

    const commonPropsUpdates = {
        lastObjectiveIdForChilds,
        lastDirectorateIdForChilds,
        lastPortfolioIdForChilds,
        lastProgrammeIdForChilds,
        lastProjectIdForChilds,
        emphasiseProjectsWithCustomField: 'SoS Priority Project',
        emphasisedProjectsHeaderText: 'SoS Priority Projects',
        otherProjectsHeaderText: 'All Other Reports',
        customerFinancialYear,
        ...routeProps
    };

    const navMenuProps = {
        manageNavExpanded,
        setManageNavExpanded,
        settingsNavExpanded,
        setSettingsNavExpanded,
        financialsNavExpanded,
        setFinancialsNavExpanded,
        hasInitialisedNavFromRoute,
        setHasInitialisedNavFromRoute,
    };

    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const toggleNavigation = () => setNavigationVisible(!isNavigationVisible);

    const { contentStyle } = useContentStyle();

    if (uc.userContext.UserId === null) return (<LoadingWhite />);

    const appName = props.menuData.filter(m => m.ID === AppIDs.PROJPORT)[0].AppName;

    const breadcrumbItems = buildManageProjectsBreadcrumb({
        customerTitle: AppGlobals.CustomerTitle,
        appName,
        location,
        history
    });


    return (
        <React.Fragment>
            <ErrorBoundary>
                <IntegrationContext.Provider value={integration}>
                    <DataContext.Provider value={ld}>
                        <OrbUserContext.Provider value={uc}>

                            <div className="main">
                                <div className="container">

                                    {/* LEFT NAVIGATION (drawer) */}
                                    <div className={`navigation ${isNavigationVisible ? 'show' : ''}`}>
                                        {!uc.userContext.UserId
                                            ? <CrLoadingOverlay isLoading={true} />
                                            : <ManageProjectsMenu {...routeProps} {...navMenuProps} onLinkClicked={toggleNavigation} />
                                        }
                                    </div>

                                    {/* BACKDROP */}
                                    <div
                                        className={`nav-backdrop ${isNavigationVisible ? 'show' : ''}`}
                                        onClick={toggleNavigation}
                                        aria-hidden={!isNavigationVisible}
                                    />

                                    {/* RIGHT CONTENT AREA */}
                                    <div className="right-area">
                                        <div className="top-banner">

                                            <MenuToggle onToggleNavigation={toggleNavigation} />

                                            <TopBanner
                                                customerTitle={AppGlobals.CustomerTitle}
                                                userName={AppGlobals.UserTitle}
                                                breadcrumbItems={breadcrumbItems}
                                                onLogout={props.onLogout}
                                            />
                                        </div>

                                        <div className="content" style={contentStyle}>
                                            <HashRouter>
                                                {/* Scroll to top on route change */}
                                                {/* <ScrollToTop /> */}
                                                <Switch>
                                                    <Route exact path="/a2/welcome" {...routeProps}>
                                                        <ManageProjectsWelcome />
                                                    </Route>

                                                    <Route exact path="/a2/insights" {...routeProps}>
                                                        <Insights dashboardRef='ProjectPortfoliosDashboard' />
                                                    </Route>

                                                    <Route exact path="/a2/ai-chats" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="AIChats" />
                                                    </Route>

                                                    <Route exact path="/a2/ask-cloe/:chatId" {...routeProps}>
                                                        <AiChat {...routeProps} />
                                                    </Route>

                                                    <DirectorateAdminRoute path="/a2/visions" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="PPVisions" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/objectives" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="PPObjectives" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/objectives-for-vision/:visionIdForObjectives"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="PPObjectives" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/portfolios" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Portfolios" />
                                                    </DirectorateAdminRoute>

                                                    {/* FINANCIALS: Portfolios */}
                                                    <DirectorateAdminRoute path="/a2/financials/portfolios" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="PortfoliosFinancials" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/portfolios-for-objective/:objectiveIdForPortfolios"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Portfolios" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/updates-for-portfolio/:portfolioIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <PortfolioUpdates {...commonPropsUpdates} />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/sign-off-for-portfolio/:portfolioIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <PortfolioSignOff {...commonPropsUpdates} />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/directorates" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Directorates" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/programmes" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Programmes" />
                                                    </DirectorateAdminRoute>

                                                    {/* FINANCIALS: Programmes */}
                                                    <DirectorateAdminRoute path="/a2/financials/programmes" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="ProgrammesFinancials" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/financials/programmes-for-portfolio/:portfolioIdForProgrammes"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="ProgrammesFinancials" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/programmes-for-portfolio/:portfolioIdForProgrammes"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Programmes" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/updates-for-programme/:programmeIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <ProgrammeUpdates {...commonPropsUpdates} />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/sign-off-for-programme/:programmeIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <ProgrammeSignOff {...commonPropsUpdates} />
                                                    </DirectorateAdminRoute>

                                                    <ProjectAdminRoute path="/a2/projects" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Projects" />
                                                    </ProjectAdminRoute>

                                                    {/* FINANCIALS: Projects */}
                                                    <ProjectAdminRoute path="/a2/financials/projects" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="ProjectsFinancials" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/projects-for-portfolio/:portfolioIdForProjects"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Projects" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/projects-for-programme/:programmeIdForProjects"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Projects" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/financials/projects-for-portfolio/:portfolioIdForProjects"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="ProjectsFinancials" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/financials/projects-for-programme/:programmeIdForProjects"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="ProjectsFinancials" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/projects-for-directorate/:directorateIdForProjects"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Projects" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/updates-for-project/:projectIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <ProjectUpdates {...commonPropsUpdates} />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/sign-off-for-project/:projectIdForUpdates/:requestWorkflowId/:requestUpdatePeriod"
                                                        {...routeProps}
                                                    >
                                                        <ProjectSignOff {...commonPropsUpdates} />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/sign-off-for-project/:projectIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <ProjectSignOff {...commonPropsUpdates} />
                                                    </ProjectAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/key-work-areas" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="KeyWorkAreas" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/key-work-areas-for-directorate/:directorateIdForKeyWorkAreas"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="KeyWorkAreas" />
                                                    </DirectorateAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/risks-for-project/:projectIdForRisks"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Risks" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/updates-for-risk/:riskIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <RiskUpdates {...commonPropsUpdates} />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/sign-off-for-risk/:riskIdForUpdates"
                                                        {...routeProps}
                                                    >
                                                        <RiskSignOff {...commonPropsUpdates} />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/risk-mitigating-actions-for-risk/:riskIdForMitigatingActions"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="RiskMitigationActions" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute path="/a2/work-streams" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="WorkStreams" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/work-streams-for-project/:projectIdForWorkStream"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="WorkStreams" />
                                                    </ProjectAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/metrics" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Metrics" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/metrics-for-directorate/:directorateIdForMetrics"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Metrics" />
                                                    </DirectorateAdminRoute>

                                                    <MilestoneAdminRoute path="/a2/milestones" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Milestones" />
                                                    </MilestoneAdminRoute>

                                                    <MilestoneAdminRoute
                                                        path="/a2/milestones-for-work-stream/:workstreamIdForMilestones"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Milestones" />
                                                    </MilestoneAdminRoute>

                                                    <MilestoneAdminRoute
                                                        path="/a2/milestones-for-work-area/:workareaIdForMilestones"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Milestones" />
                                                    </MilestoneAdminRoute>

                                                    <ProjectAdminRoute path="/a2/benefits" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Benefits" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/benefits-for-project/:projectIdForBenefits"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Benefits" />
                                                    </ProjectAdminRoute>

                                                    <DirectorateAdminRoute path="/a2/commitments" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Commitments" />
                                                    </DirectorateAdminRoute>

                                                    <DirectorateAdminRoute
                                                        path="/a2/commitments-for-directorate/:directorateIdForCommitments"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Commitments" />
                                                    </DirectorateAdminRoute>

                                                    <ProjectAdminRoute path="/a2/dependencies" {...routeProps}>
                                                        <PerformanceReportingAdmin {...commonProps} entity="Dependencies" />
                                                    </ProjectAdminRoute>

                                                    <ProjectAdminRoute
                                                        path="/a2/dependencies-for-project/:projectIdForDependencies"
                                                        {...routeProps}
                                                    >
                                                        <PerformanceReportingAdmin {...commonProps} entity="Dependencies" />
                                                    </ProjectAdminRoute>

                                                    {/* SETTINGS */}
                                                    <AdminRoute path="/a2/settings/benefit-types" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="BenefitTypes" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/reporting-frequencies" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="ReportingFrequencies" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/risk-types" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="RiskTypes" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/risk-discussion-forums" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="RiskDiscussionForums" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/units" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="Units" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/thresholds" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="Thresholds" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/threshold-appetites" {...routeProps}>
                                                        <SystemAdministration {...routeProps} entity="ThresholdAppetites" />
                                                    </AdminRoute>

                                                    <AdminRoute path="/a2/settings/update-financial-year" {...commonProps}>
                                                        <CustomerFinancialYear {...commonProps} />
                                                    </AdminRoute>

                                                    <Route path="/a2/outputs" {...routeProps}>
                                                        <ManageProjectsReport {...routeProps} />
                                                    </Route>

                                                    <Route path="/not-authorised">
                                                        <NotAuthorised />
                                                    </Route>

                                                    <Route path="*">
                                                        <NotFound />
                                                    </Route>

                                                </Switch>
                                            </HashRouter>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </OrbUserContext.Provider>
                    </DataContext.Provider>
                </IntegrationContext.Provider>
            </ErrorBoundary>
        </React.Fragment>
    );
};

export const ManageProjectsAppWithErrorHandling =
    withErrorHandling(ManageProjectsApp);